import { createAuth0Client, Auth0Client, GetTokenSilentlyOptions, User } from "@auth0/auth0-spa-js";
import { inject, provide, ref, Ref, watch } from "vue";
import router from "@/Router/router";
import { Auth0 } from '@/Services/Auth0/auth0-interface'
import { injectStrict } from '@/Services/Helper/inject-typed'
import { AxiosKey } from '@/Services/HttpClient/symbols'
import apiClient, { isAxiosError } from '@/Services/HttpClient/axios-plugin'


const domain = process.env.VUE_APP_AUTH0_DOMAIN;
const clientId = process.env.VUE_APP_AUTH0_CLIENT_ID;
const audience = process.env.VUE_APP_AUTH0_AUDIENCE;
const callbackUrl = process.env.VUE_APP_AUTH0_CALLBACK_URL;

const axios = apiClient

const auth0Client: Ref<Auth0Client | null> = ref(null);
export const isAuthenticated: Ref<boolean> = ref(false);
export const isLoading: Ref<boolean> = ref(true);
const user = ref<User | null>(null);
const error: Ref<Error | null> = ref(null);
const userCreated = ref(false); // Prevent duplicate user creation from the watch


export const Auth0Symbol: symbol = Symbol();

const createClient = async () => {

    auth0Client.value = await createAuth0Client({
        domain,
        clientId: clientId,
        authorizationParams: {
            audience: audience,
            redirect_uri: callbackUrl,
        }
    });
};

const handleCallback = async (): Promise<void> => {
    if (!auth0Client.value) {
        console.log('auth0 plugin, handleCallback, auth0Client has no value.');
        return;
    }
    console.log('handling callback');
    
    isAuthenticated.value = await auth0Client.value.isAuthenticated();

    console.log('isAuthenticated initial: ' + isAuthenticated.value)
    
    if (isAuthenticated.value) {
        console.log('isAuthenticated: about to Get User ' + isAuthenticated.value)
        user.value = (await auth0Client.value.getUser()) || null;
        isLoading.value = false;
        console.log('calling fetch/create');
        await fetchOrCreateUser(); // Ensure API call happens
        window.history.replaceState({}, document.title, window.location.pathname);

        return;
    }

    const params = new URLSearchParams(window.location.search);
    const hasError = params.has("error");
    const hasCode = params.has("code");
    const hasState = params.has("state");

    if (hasError) {
        error.value = new Error(
            params.get("error_description") || "error completing login process"
        );

        isLoading.value = false;
        console.log('handleCallback hasError: ' + error.value);

        return;
    }

    if (hasCode && hasState) {
        try {
            //console.log('handleCallback hasCode and hasState');
            const result = await auth0Client.value.handleRedirectCallback();

            let url: string = "/";

            if (result.appState && result.appState.targetUrl) {
                url = result.appState.targetUrl;
            }

            isAuthenticated.value = await auth0Client.value.isAuthenticated();
            //console.log('handleCallback hasCode and hasState isAuthenticatedState: ' + isAuthenticated.value);
            if (isAuthenticated.value) {
                user.value = (await auth0Client.value.getUser()) || null;
                sessionStorage.setItem("auth0_userId", user.value ? user.value.sub as string : "");

                await fetchOrCreateUser(); // Ensure API call happens

                isLoading.value = false;
                window.history.replaceState({}, document.title, url);
                await router.push(url);
                return;
            }
        } catch (err) {
            error.value = err as Error;
        }
    }

    isLoading.value = false;
};

const fetchOrCreateUser = async (retries = 3) => {
    if (userCreated.value) {
        console.log("fetchOrCreateUser: Skipping duplicate API call.");
        return;
    }

    console.log("in fetch/create");
    if (!user.value) {
        console.error("fetchOrCreateUser: User object is null");
        return;
    }

    userCreated.value = true; // Mark execution to prevent duplicates

    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) {
                console.error("fetchOrCreateUser: Failed to get access token");
                return;
            }

            console.log(`Calling API to fetch or create user - Attempt ${attempt}`);

            const modifiedDate = new Date();
            const userData = {
                emailAddress: user.value.email as string,
                auth0UserId: user.value.sub as string,
                firstName: user.value.given_name as string,
                lastName: user.value.family_name as string,
                isActive: true,
                createdBy: "Registration",
                createdDate: modifiedDate.toISOString(),
                modifiedBy: "Registration",
                modifiedDate: modifiedDate.toISOString(),
            };

            const response = await axios.post("api/User/GetUserDtoOrCreateByAuth0UserId", userData, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            //console.log("User API response:", response.data);
            return; // Exit function if successful
        } catch (e: unknown) {
            if (isAxiosError(e)) {
                console.error(`API Error (Attempt ${attempt}):`, e.response?.data);
            } else {
                console.error(`Unknown error in fetchOrCreateUser (Attempt ${attempt}):`, e);
            }
        }
        // Wait before retrying (Exponential Backoff)
        await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
    }
    console.error("fetchOrCreateUser failed after all retries");
};


export const login = async (options?: Record<string, unknown>): Promise<void> => {
    if (!auth0Client.value) {
        return;
    }

    try {
        await auth0Client.value.loginWithRedirect(options);
    } catch (err) {
        error.value = err as Error;
    }
};

const logout = async (options?: Record<string, unknown>): Promise<void> => {
    if (!auth0Client.value) {
        return;
    }

    try {
        auth0Client.value.logout(options);
    } catch (err) {
        error.value = err as Error;
    }
};

const getAccessToken = async (
    options?: Record<string, unknown>
): Promise<string | null> => {
    if (!auth0Client.value) {
        return null;
    }

    options = {
        scope: 'openid profile email',
    };

    return await auth0Client.value.getTokenSilently(options);
};

//In some cases we have services that are loaded and used before the main app root has been loaded and the Auth0 client has been properly injected (eliminating the usefulness of useAuth();) 
//We don't want to expose the client directly, or create another client instance. So we must use this middle ground. Please DON'T use this for anything beyond external services.
export const getAccessTokenForService = async (options?: GetTokenSilentlyOptions): Promise<null | string> => {
    if (!auth0Client.value) {
        return null;
    }

    options = {
        authorizationParams: {
            scope: 'openid profile email',
        }
    };

    return (await auth0Client.value.getTokenSilently(options)) as string;
};

export const provideAuth0 = () => {
    const auth0: Auth0 = {
        isAuthenticated,
        isLoading,
        user,

        createClient,
        handleCallback,
        login,
        logout,
        getAccessToken,
    };

    provide(Auth0Symbol, auth0);

    // Watch for user updates and ensure fetchOrCreateUser runs when needed
    watch(user, async (newUser) => {
        if (newUser && isAuthenticated.value) {
            console.log("User value updated, ensuring user exists.");
            await fetchOrCreateUser();
        }
    });
};


export const useAuth0 = (): Auth0 | null => {
    const auth0 = inject(Auth0Symbol) as Auth0 | null;
    return auth0 || null;
};
